// src/pages/Services.js
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "../App.css";
import { useInView } from "../hooks/useInView";
import { useNavigate } from "react-router-dom";
import { useCareModal } from "../context/CareModalContext";
import {
  FaInfoCircle,
  FaHeartbeat,
  FaHandsHelping,
  FaPhoneAlt,
  FaClipboardCheck,
} from "react-icons/fa";
import logo from "../images/logo.png";

const Services = () => {
  const services = [
    {
      icon: "🏠",
      title: "Domiciliary Care",
      text:
        "Personal care, meal preparation, and medication support in the comfort of your home.",
      detailPoints: [
        "Help with washing, dressing and personal hygiene",
        "Support with meals, drinks and nutrition",
        "Medication prompts and basic health monitoring",
      ],
    },
    {
      icon: "🤝",
      title: "Supported Living",
      text:
        "Promoting independence through tailored support for daily activities and community engagement.",
      detailPoints: [
        "Encouraging independent living skills",
        "Support with budgeting, appointments and travel",
        "Flexible staffing to match individual needs",
      ],
    },
    {
      icon: "💬",
      title: "Companionship",
      text:
        "Friendly carers offering emotional and social support through daily visits or calls.",
      detailPoints: [
        "Regular social visits and conversation",
        "Support with hobbies, interests and outings",
        "Reducing isolation and loneliness",
      ],
    },
    {
      icon: "🌙",
      title: "Night Care",
      text: "Overnight assistance for safety, rest, and peace of mind.",
      detailPoints: [
        "Waking or sleeping night support packages",
        "Reassurance for families when they cannot be present",
        "Help with overnight toileting and mobility",
      ],
    },
    {
      icon: "🕒",
      title: "Respite Care",
      text:
        "Temporary relief for families and caregivers needing trusted short-term care.",
      detailPoints: [
        "Planned breaks for family carers",
        "Flexible durations from a few hours to several weeks",
        "Continuity of care with trained staff",
      ],
    },
    {
      icon: "🧩",
      title: "Specialist Support",
      text:
        "Care for individuals with disabilities or special needs, designed with empathy and expertise.",
      detailPoints: [
        "Support for physical, learning and complex needs",
        "Person-centred plans developed with families",
        "Close alignment with health and therapy teams",
      ],
    },
  ];

  const [gridRef, gridInView] = useInView();
  const navigate = useNavigate();
  const { openBookCare, openJoinTeam } = useCareModal();

  // Local state for service-specific modals
  const [selectedService, setSelectedService] = useState(null);
  const [isServiceInfoOpen, setIsServiceInfoOpen] = useState(false);
  const [isServiceRequestOpen, setIsServiceRequestOpen] = useState(false);

  // Request Care modal form state
  const [requestForm, setRequestForm] = useState({
    name: "",
    phone: "",
    email: "",
    notes: "",
  });
  const [requestErrors, setRequestErrors] = useState({});

  const openServiceInfo = (service) => {
    setSelectedService(service);
    setIsServiceInfoOpen(true);
  };

  const closeServiceInfo = () => {
    setIsServiceInfoOpen(false);
    setSelectedService(null);
  };

  const openServiceRequest = (service) => {
    setSelectedService(service);
    setIsServiceRequestOpen(true);
  };

  const closeServiceRequest = () => {
    setIsServiceRequestOpen(false);
    setSelectedService(null);
    setRequestForm({
      name: "",
      phone: "",
      email: "",
      notes: "",
    });
    setRequestErrors({});
  };

  const handleRequestChange = (e) => {
    const { name, value } = e.target;
    setRequestForm((prev) => ({ ...prev, [name]: value }));
    setRequestErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateRequest = () => {
    const errors = {};
    if (!requestForm.name.trim()) {
      errors.name = "Full name is required.";
    }
    if (!requestForm.phone.trim()) {
      errors.phone = "Contact number is required.";
    }
    if (requestForm.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(requestForm.email.trim())) {
        errors.email = "Please enter a valid email address.";
      }
    }
    return errors;
  };

  const handleRequestSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateRequest();
    if (Object.keys(validationErrors).length > 0) {
      setRequestErrors(validationErrors);
      return;
    }

    // TODO: send to backend / email
    console.log("Service request", {
      service: selectedService?.title,
      ...requestForm,
    });

    closeServiceRequest();
  };

  // Helper to portal a modal to the document body
  const portal = (node) => {
    if (!node) return null;
    if (typeof document === "undefined") return null;
    return createPortal(node, document.body);
  };

  return (
    <>
      {/* HERO */}
      <section className="hero services-hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">What We Offer</h1>
          <p className="hero-subtitle">
            Delivering safe, compassionate, and consistent care tailored to
            every individual&apos;s needs.
          </p>

          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={openBookCare}>
              Book For Care
            </button>
            <button className="btn btn-light" onClick={openJoinTeam}>
              Join Our Team
            </button>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section services-page">
        <h2 className="section-title">Services</h2>

        <div ref={gridRef} className="services-page-grid">
          {services.map((service, index) => (
            <article
              key={service.title}
              className={`service-card service-card-full stagger-item ${
                gridInView ? "in-view" : ""
              }`}
              style={{ "--delay": `${index * 0.12}s` }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.text}</p>

              <div className="service-card-actions">
                <button
                  className="btn-chip"
                  onClick={() => openServiceInfo(service)}
                >
                  Learn More
                </button>
                <button
                  className="btn-chip btn-chip-outline"
                  onClick={() => openServiceRequest(service)}
                >
                  Request Care
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* SERVICE INFO MODAL (PORTALED, CENTERED) */}
      {portal(
        isServiceInfoOpen &&
          selectedService && (
            <div className="modal-backdrop" onClick={closeServiceInfo}>
              <div
                className="modal modal-animate service-info-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="modal-close-btn"
                  onClick={closeServiceInfo}
                  aria-label="Close"
                >
                  ✕
                </button>

                <div className="book-care-header">
                  <div className="book-care-logo-wrap service-modal-logo-wrap">
                    <img
                      src={logo}
                      alt="Ndils Daily Care Logo"
                      className="book-care-logo"
                    />
                  </div>
                  <div className="book-care-title-block">
                    <h3>{selectedService.title}</h3>
                    <p className="service-modal-tagline">
                      Safe, person-centred support delivered by trained
                      professionals.
                    </p>
                  </div>
                </div>

                <div className="service-modal-body">
                  <div className="service-modal-highlight">
                    <div className="service-modal-icon-circle">
                      <span className="service-modal-emoji">
                        {selectedService.icon}
                      </span>
                    </div>
                    <div className="service-modal-highlight-text">
                      <p>{selectedService.text}</p>
                      <div className="service-modal-pill-row">
                        <span className="service-modal-pill">
                          <FaHeartbeat /> Compassion-led
                        </span>
                        <span className="service-modal-pill">
                          <FaHandsHelping /> Family-focused
                        </span>
                      </div>
                    </div>
                  </div>

                  {selectedService.detailPoints && (
                    <div className="service-modal-list">
                      <h4>
                        <FaInfoCircle className="service-modal-list-icon" />{" "}
                        What this service can include:
                      </h4>
                      <ul>
                        {selectedService.detailPoints.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="service-modal-footer-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        closeServiceInfo();
                        openServiceRequest(selectedService);
                      }}
                    >
                      Request This Service
                    </button>
                    <button
                      className="btn btn-light-outline"
                      onClick={() => navigate("/contact")}
                    >
                      Contact Our Team
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
      )}

      {/* SERVICE REQUEST MODAL (PORTALED) */}
      {portal(
        isServiceRequestOpen &&
          selectedService && (
            <div className="modal-backdrop" onClick={closeServiceRequest}>
              <div
                className="modal service-request-modal modal-animate"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="modal-close-btn"
                  onClick={closeServiceRequest}
                  aria-label="Close"
                >
                  ✕
                </button>

                <div className="book-care-header">
                  <div className="book-care-logo-wrap service-modal-logo-wrap">
                    <img
                      src={logo}
                      alt="Ndils Daily Care Logo"
                      className="book-care-logo"
                    />
                  </div>
                  <div className="book-care-title-block">
                    <h3>Request {selectedService.title}</h3>
                    <p className="service-modal-tagline">
                      Share your details and we&apos;ll call you back to discuss
                      a tailored care plan.
                    </p>
                  </div>
                </div>

                <form
                  className="book-care-form"
                  onSubmit={handleRequestSubmit}
                  noValidate
                >
                  <div className="service-request-summary">
                    <div className="service-request-badge">
                      <span className="service-request-emoji">
                        {selectedService.icon}
                      </span>
                      <div>
                        <p className="service-request-label">
                          Service Selected
                        </p>
                        <p className="service-request-title">
                          {selectedService.title}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="book-care-row">
                    <div
                      className={`book-care-field ${
                        requestErrors.name ? "field-error" : ""
                      }`}
                    >
                      <label>
                        <span className="field-icon">
                          <FaClipboardCheck />
                        </span>
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={requestForm.name}
                        onChange={handleRequestChange}
                        placeholder="Enter your full name"
                        required
                      />
                      {requestErrors.name && (
                        <p className="error-text">{requestErrors.name}</p>
                      )}
                    </div>

                    <div
                      className={`book-care-field ${
                        requestErrors.phone ? "field-error" : ""
                      }`}
                    >
                      <label>
                        <span className="field-icon">
                          <FaPhoneAlt />
                        </span>
                        Contact Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={requestForm.phone}
                        onChange={handleRequestChange}
                        placeholder="+44 ..."
                        required
                      />
                      {requestErrors.phone && (
                        <p className="error-text">{requestErrors.phone}</p>
                      )}
                    </div>
                  </div>

                  <div
                    className={`book-care-field ${
                      requestErrors.email ? "field-error" : ""
                    }`}
                  >
                    <label>Email Address (optional)</label>
                    <input
                      type="email"
                      name="email"
                      value={requestForm.email}
                      onChange={handleRequestChange}
                      placeholder="you@example.com"
                    />
                    {requestErrors.email && (
                      <p className="error-text">{requestErrors.email}</p>
                    )}
                  </div>

                  <div className="book-care-field">
                    <label>Brief Notes (optional)</label>
                    <textarea
                      name="notes"
                      rows="4"
                      value={requestForm.notes}
                      onChange={handleRequestChange}
                      placeholder="Tell us anything specific about the support you’re looking for..."
                    />
                  </div>

                  <div className="book-care-footer">
                    <p className="book-care-note">
                      A member of the Ndils Daily Care team will be in touch as
                      soon as possible. We treat all enquiries with sensitivity
                      and confidentiality.
                    </p>
                    <button type="submit" className="btn btn-primary">
                      Submit Request
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )
      )}
    </>
  );
};

export default Services;
